import type { ReactNode } from 'react'
import { useMemo } from 'react'
import {
  Breadcrumb,
  Breadcrumbs,
  colors,
  getTargetedPortal,
  spacing,
} from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { FormattedMessage, useIntl } from 'react-intl'
import { type SiteOperationIntent } from '../../common/site-select/SiteSelect'
import { type FilterSitesFn } from '../retailer-and-site-picker/RetailerAndSitePicker.types'
import { StoreConfigScopeWrapper } from './StoreConfigScopeWrapper'
import { BannerSelectRequired } from './BannerSelectRequired'
import { ScopeSelector } from './ScopeSelector'
import { ViewType } from './ScopeSelection.types'
import { useIsRetailerSelectRequired, useStoreConfigData } from './ScopeSelection.hooks'

export interface RetailerScopeWrapperProps {
  children?: ReactNode
  selectionType?: ViewType
  retailerSelectionOptional?: boolean
  storeConfigSelectionOptional?: boolean
  storeConfigOperationIntent?: SiteOperationIntent // 'view' or 'edit'
  storeConfigFilter?: FilterSitesFn // store config filters
  titles?: string[]
  formattedMessageTitle?: string
  retailerInsufficientPermission?: boolean
  hiddenScopePicker?: boolean
  multiSelect?: boolean
  allowNotVisibleToPartnersStoreConfigs?: boolean
}

const Header = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: `${spacing.X16} ${spacing.X16}`,
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
})

const Toolbar = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

export const [HeaderTitlePortalTarget, HeaderTitlePortal] = getTargetedPortal()

export const RetailerScopeWrapper = ({
  children,
  retailerSelectionOptional,
  storeConfigSelectionOptional,
  selectionType,
  storeConfigOperationIntent,
  storeConfigFilter,
  titles,
  formattedMessageTitle,
  retailerInsufficientPermission,
  hiddenScopePicker,
  multiSelect,
  allowNotVisibleToPartnersStoreConfigs,
}: RetailerScopeWrapperProps) => {
  const { storeConfigId, storeConfigIds, storeConfigurations } = useStoreConfigData()
  const toolContentVisible = !useIsRetailerSelectRequired({
    selectionType,
    retailerSelectionOptional,
    storeConfigSelectionOptional,
    hiddenScopePicker,
  })

  selectionType = selectionType || ViewType.RETAILER

  // if scope picker is optional, then override retailerSelectionOptional, storeConfigSelectionOptional
  retailerSelectionOptional ||= hiddenScopePicker
  storeConfigSelectionOptional ||= hiddenScopePicker

  const intl = useIntl()
  const headerComponent = useMemo(() => {
    if (titles) {
      const breadcrumbs = titles?.map(title => (
        <Breadcrumb key={title}>{intl.formatMessage({ id: title }) || title}</Breadcrumb>
      ))
      return <Breadcrumbs>{breadcrumbs}</Breadcrumbs>
    } else if (formattedMessageTitle) {
      return (
        <Breadcrumbs>
          <Breadcrumb>
            <FormattedMessage id={formattedMessageTitle} />
          </Breadcrumb>
        </Breadcrumbs>
      )
    }
  }, [formattedMessageTitle, intl, titles])

  const storeConfigs = storeConfigurations?.data?.storeConfigurationsByRetailerIds

  children = toolContentVisible ? children : null

  if (
    selectionType == ViewType.RETAILER_AND_STORE_CONFIG ||
    selectionType == ViewType.STORE_CONFIG
  ) {
    children = (
      <StoreConfigScopeWrapper
        storeConfig={storeConfigs?.find(e => e.id === String(storeConfigId))}
        storeConfigs={storeConfigs?.filter(e => storeConfigIds.includes(e.id))}
      >
        {children}
      </StoreConfigScopeWrapper>
    )
  }

  return (
    <>
      <Header>
        {toolContentVisible ? <HeaderTitlePortalTarget /> : headerComponent}
        {!hiddenScopePicker && (
          <>
            <Toolbar>
              <ScopeSelector
                selectionType={selectionType}
                retailerSelectionOptional={retailerSelectionOptional}
                storeConfigOperationIntent={storeConfigOperationIntent}
                storeConfigFilter={storeConfigFilter}
                retailerInsufficientPermission={retailerInsufficientPermission}
                multiSelect={multiSelect}
                allowNotVisibleToPartnersStoreConfigs={allowNotVisibleToPartnersStoreConfigs}
              />
            </Toolbar>
          </>
        )}
      </Header>
      {toolContentVisible ? children : <BannerSelectRequired selectionType={selectionType} />}
    </>
  )
}
